import { AppConfig } from "../config/appConfig";

/**
 * A singleton that watches the HWI Online UI host for application updates.
 */
export class UpdateManager {
	private static _instance: UpdateManager;
	private readonly _isDev = AppConfig.environment.name === "dev";
	private readonly _updateCheckPollCooldownMs = 1000 * 60 * 60; // Check every hour when idle
	private readonly _updateCheckFetchCooldownMs = 1000 * 60; // Don't check-on-poke more than once per minute
	private _lastUpdateCheck: Date = new Date(0);
	private _pollReference: NodeJS.Timeout = null;
	private _lastKnownVersion: IAppVersion = null;

	private constructor(showDebugLogging: boolean = false) {
		this.showDebugLogging = showDebugLogging;
		// If we're in a dev environment, enable debug logging by default
		if (this._isDev) {
			this._info("Disabled in development environment");
			return;
		}
		this._debug("UpdateManager initialized");
		this._resetUpdatePollTimer();
	}

	/** 
	 * Shows debug messages in the console if true. 
	 */
	public showDebugLogging: boolean = false;	

	/**	
	 * Get the UpdateManager singleton's instance
	 * @returns An UpdateManager instance
	 */
	public static getInstance(showDebugLogging: boolean = false): UpdateManager {
		if (!this._instance) {
			this._instance = new UpdateManager(showDebugLogging);
		}
		this._instance.showDebugLogging = showDebugLogging;
		return this._instance;
	}

	/**
	 * Request an immediate check for application updates.
	 */
	public static poke(): void {
		if (this._instance) {
			this._instance._checkForUpdate();
		}
		else {
			console.warn("UpdateManager: Poke failed - called before UpdateManager instance was created.");
		}
	}

	//#region Logging methods
	private _consoleMessage(message: string, level: string = "info", ...optionalParams: any[]): void {
		console.log(`[${new Date().toLocaleString("en-US", { hour12: false })}] [${level.toUpperCase()}] UpdateManager: ${message}`, ...optionalParams);
	}
	private _debug(message: string, ...optionalParams: any[]): void {
		if (this.showDebugLogging) {
			this._consoleMessage(message, "debug", ...optionalParams);
		}
	}
	private _info(message: string, ...optionalParams: any[]): void {
		this._consoleMessage(message, "info", ...optionalParams);
	}
	private _warn(message: string, ...optionalParams: any[]): void {
		this._consoleMessage(message, "warn", ...optionalParams);
	}
	private _error(message: string, ...optionalParams: any[]): void {
		this._consoleMessage(message, "error", ...optionalParams);
	}
	//#endregion Logging methods

	//#region Update checking methods
	private _isFetchOnCooldown(): boolean {
		return (new Date().valueOf() - this._lastUpdateCheck.valueOf()) < this._updateCheckFetchCooldownMs;		
	}

	private async _checkForUpdate(): Promise<boolean> {
		try {
			if (this._isDev) return;
			if (this._isFetchOnCooldown()) {
				this._debug(`Skipping update check - fetch is on cooldown. ${this._updateCheckFetchCooldownMs - (new Date().valueOf() - this._lastUpdateCheck.valueOf())}ms remaining`);
				return false;
			}
			this._lastUpdateCheck = new Date();

			// Try to fetch the main HTML page to see if it has changed
			const response = await fetch('/', {
				cache: 'no-cache',
				headers: {
					'Cache-Control': 'no-cache, no-store, must-revalidate'
				}
			});

			if (response.ok) {
				const htmlContent = await response.text();

				// Extract script and link tags to see if bundle names have changed
				const jsMatches = htmlContent.matchAll(/<script[^>]*src="\/static\/js\/main[\.?]([^\.]*).js"[^>]*>/g);
				const cssMatches = htmlContent.matchAll(/<link[^>]*href="\/static\/css\/main[\.?]([^\.]*).css"[^>]*>/g);

				const currentVersion: IAppVersion = {
					js: Array.from(jsMatches).map(m => m[1]).join('|'),
					css: Array.from(cssMatches).map(m => m[1]).join('|')
				};

				if (this._lastKnownVersion === null) {
					this._lastKnownVersion = currentVersion;
					return false;
				}

				if (currentVersion.js !== this._lastKnownVersion.js || currentVersion.css !== this._lastKnownVersion.css) {
					this._debug('Application version update detected');
					this._publishUpdateDetected(currentVersion);
					this._lastKnownVersion = currentVersion;
					return true;
				}

				this._debug('No application update detected');
				return false;
			}
		} catch (error) {
			this._warn('Failed to check for application update:', error);
		} finally {
			this._resetUpdatePollTimer();
		}

		return false;
	}
	//#endregion Update checking methods

	//#region Idle poll timer methods
	private _resetUpdatePollTimer(timeoutMs: number = this._updateCheckPollCooldownMs): void {
		this._debug("Resetting update poll timer...");
		this._cancelUpdatePollTimer();
		this._pollReference = setTimeout(() => {
			this._checkForUpdate();
		}, timeoutMs);
		this._debug(`Update poll timer started for ${timeoutMs}ms`);
	}

	private _cancelUpdatePollTimer() {
		this._debug('Cancelling current update poll timer');
		if (this._pollReference) {
			clearTimeout(this._pollReference);
			this._pollReference = null;
		}
	}
	//#endregion Idle poll timer methods

	//#region onUpdateDetected event
	private _updateDetectedSubscriptions: ((event: UpdateDetectedEvent) => void)[] = [];

	/**
	 * Subscribe to onUpdateDetected events.
	 * @param callback The callback to invoke when an update is detected.
	 * @returns True if the subscription was successful, false if it was already subscribed.
	 */
	public onUpdateDetectedSubscribe(callback: (event: UpdateDetectedEvent) => void): boolean {
		if (!this.isOnUpdateDetectedSubscribed(callback)) {
			this._updateDetectedSubscriptions.push(callback);
			return true;
		}
		return false;
	}

	/**
	 * Unsubscribe from onUpdateDetected events.
	 * @param callback The callback to remove from the subscription list.
	 * @returns True if the unsubscription was successful, false if it was not already subscribed.
	 */
	public onUpdateDetectedUnsubscribe(callback: (event: UpdateDetectedEvent) => void): boolean {
		if (this.isOnUpdateDetectedSubscribed(callback)) {
			this._updateDetectedSubscriptions = this._updateDetectedSubscriptions.filter(c => c != callback);
			return true;
		}
		return false;
	}

	/**
	 * Check if a callback is already subscribed to onUpdateDetected events.
	 * @param callback The callback to check for.
	 * @returns True if the callback is subscribed, false otherwise.
	 */
	public isOnUpdateDetectedSubscribed(callback: (event: UpdateDetectedEvent) => void): boolean {
		return this._updateDetectedSubscriptions.indexOf(callback) != -1;
	}

	/**
	 * Publish an onUpdateDetected event to all subscribers.
	 * @param newVersion The new application version that was detected.
	 */
	private _publishUpdateDetected(newVersion: IAppVersion): void {
		const event = new UpdateDetectedEvent(this._lastKnownVersion, newVersion);
		this._debug('Publishing UpdateDetected', event);
		this._updateDetectedSubscriptions.forEach(callback => {
			try {
				callback(event);
			} catch (e) {
				this._error("Error in UpdateDetected subscription callback: ", e);
			}
		});
	}
	//#endregion onUpdateDetected event
};

export class UpdateDetectedEvent extends Event {
	public currentVersion: IAppVersion;
	public newVersion: IAppVersion;

	constructor(currentVersion: IAppVersion, newVersion: IAppVersion) {
		super("update-detected");
		this.currentVersion = currentVersion;
		this.newVersion = newVersion;
	}

	get isJsUpdate(): boolean {
		return this.currentVersion?.js != this.newVersion?.js;
	}

	get isCssUpdate(): boolean {
		return this.currentVersion?.css != this.newVersion?.css;
	}
}

export class IAppVersion {
	js: string;
	css: string;
}
